import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import { useMutation, useQuery } from "@tanstack/react-query";

export type TeamCreateSchema = components["schemas"]["TeamCreateSchema"];

export function createTeam() {
  return useMutation({
    mutationFn: async (teamData: TeamCreateSchema) => {
      const { data, error } = await client.get().POST("/api/teams", {
        body: teamData,
      });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to create team");
    },
  });
}

export function updateTeam() {
  return useMutation({
    mutationFn: async ({
      teamId,
      teamData,
    }: {
      teamId: number;
      teamData: TeamCreateSchema;
    }) => {
      const { data, error } = await client.get().PUT("/api/teams/{team_id}", {
        params: {
          path: {
            team_id: teamId,
          },
        },
        body: teamData,
      });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to update team");
    },
  });
}

export function getTeams({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["teams"],
    enabled,
    queryFn: async ({ signal }) => {
      const { data, error } = await client.get().GET("/api/teams", {
        signal,
      });
      if (data) return data;
      throw new Error(String(error));
    },
  });
}

export function getTeamMembers({
  teamId,
  enabled = true,
}: {
  teamId?: number;
  enabled?: boolean;
} = {}) {
  return useQuery({
    queryKey: ["team_members", teamId],
    enabled,
    queryFn: async ({ signal }) => {
      const { data, error } = await client.get().GET("/api/team_members", {
        params: {
          query: teamId ? { team_id: teamId } : {},
        },
        signal,
      });
      if (data) return data;
      throw new Error(String(error));
    },
  });
}

export type InviteMemberSchema = components["schemas"]["InviteMemberSchema"];

export function inviteTeamMember() {
  return useMutation({
    mutationFn: async ({
      teamId,
      inviteData,
    }: {
      teamId: number;
      inviteData: InviteMemberSchema;
    }) => {
      const { data, error } = await client
        .get()
        .POST("/api/teams/{team_id}/invite", {
          params: {
            path: {
              team_id: teamId,
            },
          },
          body: inviteData,
        });

      if (error) {
        console.warn("Failed to invite team member:", error);
        // Extract the detailed error message from the API response
        let errorDetail = "Failed to send invitation";

        try {
          const apiError = error as Record<string, unknown>;
          if (apiError?.detail) {
            const detailValue = apiError.detail;
            errorDetail =
              typeof detailValue === "string"
                ? detailValue
                : Array.isArray(detailValue)
                  ? detailValue.join(", ")
                  : JSON.stringify(detailValue);
          }
        } catch {
          // If error parsing fails, use default message
          errorDetail = "Failed to send invitation";
        }

        throw new Error(errorDetail);
      }

      if (data) return data;
      throw new Error("No data received from invitation request");
    },
  });
}

export function removeTeamMember() {
  return useMutation({
    mutationFn: async ({
      teamId,
      memberId,
    }: {
      teamId: number;
      memberId: number;
    }) => {
      const { data, error } = await client
        .get()
        .DELETE("/api/teams/{team_id}/members/{member_id}", {
          params: {
            path: {
              team_id: teamId,
              member_id: memberId,
            },
          },
        });

      if (error) {
        console.warn("Failed to remove team member:", error);
        // Extract the detailed error message from the API response
        let errorDetail = "Failed to remove team member";

        try {
          const apiError = error as Record<string, unknown>;
          if (apiError?.detail) {
            const detailValue = apiError.detail;
            errorDetail =
              typeof detailValue === "string"
                ? detailValue
                : Array.isArray(detailValue)
                  ? detailValue.join(", ")
                  : JSON.stringify(detailValue);
          }
        } catch {
          // If error parsing fails, use default message
          errorDetail = "Failed to remove team member";
        }

        throw new Error(errorDetail);
      }

      if (data) return data;
      throw new Error("No data received from remove request");
    },
  });
}

export function removeTeam() {
  return useMutation({
    mutationFn: async ({ teamId }: { teamId: number }) => {
      const { data, error } = await client
        .get()
        .DELETE("/api/teams/{team_id}", {
          params: {
            path: {
              team_id: teamId,
            },
          },
        });

      if (error) {
        console.warn("Failed to remove team:", error);
        // Extract the detailed error message from the API response
        let errorDetail = "Failed to remove team";

        try {
          const apiError = error as Record<string, unknown>;
          if (apiError?.detail) {
            const detailValue = apiError.detail;
            errorDetail =
              typeof detailValue === "string"
                ? detailValue
                : Array.isArray(detailValue)
                  ? detailValue.join(", ")
                  : JSON.stringify(detailValue);
          }
        } catch {
          // If error parsing fails, use default message
          errorDetail = "Failed to remove team";
        }

        throw new Error(errorDetail);
      }

      if (data) return data;
      throw new Error("No data received from remove team request");
    },
  });
}

export function acceptInvitation() {
  return useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      const { data, error } = await client
        .get()
        .POST("/api/invitations/accept", {
          body: {
            token,
          },
        });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to accept invitation");
    },
  });
}

export function declineInvitation() {
  return useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      const { data, error } = await client
        .get()
        .POST("/api/invitations/decline", {
          body: {
            token,
          },
        });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to decline invitation");
    },
  });
}

export function getInvitationDetails({
  token,
  enabled = true,
}: {
  token: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["invitation", token],
    enabled: enabled && !!token,
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/invitations/{token}", {
          params: {
            path: {
              token,
            },
          },
          signal,
        });
      if (data) return data;
      throw new Error(String(error));
    },
  });
}
